import {createAction} from '@reduxjs/toolkit'
import {Ticket} from '@wix/ambassador-wix-events-web/types'
import {
  ExperimentNames,
  ExtendedListScheduleItemsResponse,
  TicketWithFormattedSalePeriod,
  getEventId,
  isDraft,
  isTicketed,
} from '@wix/wix-events-commons-statics'
import {callAPI, createActions} from '../../../../commons/actions/data-action-helper'
import {updateDates} from '../../../../commons/actions/dates'
import {DETAILS_ROUTE} from '../../../../commons/constants/navigation'
import {
  isEventsInMembersInstalled,
  isGroupsInstalled,
  isPaidPlansInstalled,
  isSchedulePageInstalled,
} from '../../../../commons/selectors/installed-apps'
import {getEvent as getEventSelector, isDemoEvent} from '../selectors/event'
import {createAsyncAction} from '../services/redux-toolkit'
import {GetState} from '../types/state'
import {StoreExtraArgs} from '../types/store-extra-arg'
import {getDraftToken} from '../utils/query'
import {getCheckoutOptions, getCheckoutOptionsOld} from './checkout-options'
import {getConnectedGroup, getConnectedGroupOld} from './groups'
import {navigate, navigateOld} from './navigation'
import {getPlanList} from './paid-plans'
import {getMemberRsvp, getMemberRsvpOld} from './rsvp'

export const GET_EVENT_OLD = createActions('GET_EVENT_OLD')
export const GET_MEMBERS_OLD = createActions('GET_MEMBERS_OLD')

export const changeEventAction = createAction<{
  event: wix.events.Event
  tickets: Ticket[] | TicketWithFormattedSalePeriod[]
  schedule: ExtendedListScheduleItemsResponse
}>('CHANGE_EVENT')

export const changeEvent =
  (slug: string) =>
  async (dispatch: Function, getState: GetState, {wixCodeApi, serverApi, flowAPI}: StoreExtraArgs) => {
    const state = getState()
    const responsive = flowAPI.environment.isEditorX
    const draftPreviewToken = getDraftToken(wixCodeApi)
    const schedulePageInstalled = isSchedulePageInstalled(state)

    const useReduxToolkitEnabled = flowAPI.experiments.enabled(ExperimentNames.UseDetailsPageReduxToolkit)

    const {event, dates, tickets, schedule} = await serverApi.getData({
      slug,
      responsive,
      schedulePageInstalled,
      draftPreviewToken,
      flowAPI,
    })
    await dispatch(updateDates(dates))
    await dispatch(changeEventAction({event, tickets, schedule}))

    useReduxToolkitEnabled ? dispatch(getMemberRsvp()) : dispatch(getMemberRsvpOld())
    dispatch(getExtraEventData())

    if (useReduxToolkitEnabled) {
      dispatch(navigate({route: DETAILS_ROUTE.DETAILS}))
    } else {
      dispatch(navigateOld(DETAILS_ROUTE.DETAILS))
    }
  }

export const getExtraEventData =
  () =>
  (dispatch: Function, getState: GetState, {flowAPI}: StoreExtraArgs): Promise<any>[] => {
    const state = getState()
    const event = getEventSelector(state)
    const promises = []
    const useDetailsPageReduxToolkitEnabled = flowAPI.experiments.enabled(ExperimentNames.UseDetailsPageReduxToolkit)

    if (event && !isDraft(event)) {
      if (useDetailsPageReduxToolkitEnabled) {
        promises.push(dispatch(getMembers()))
      } else {
        promises.push(dispatch(getMembersOld()))
      }

      if (isPaidPlansInstalled(state)) {
        promises.push(dispatch(getPlanList()))
      }

      if (isTicketed(event)) {
        if (useDetailsPageReduxToolkitEnabled) {
          promises.push(dispatch(getCheckoutOptions()))
        } else {
          promises.push(dispatch(getCheckoutOptionsOld()))
        }
      }

      if (isGroupsInstalled(state)) {
        if (useDetailsPageReduxToolkitEnabled) {
          promises.push(dispatch(getConnectedGroup()))
        } else {
          promises.push(dispatch(getConnectedGroupOld()))
        }
      }
    }

    return promises
  }

export const getEventOld = () => (dispatch: Function, getState: GetState) => {
  const state = getState()
  const event = getEventSelector(state)

  if (!isDemoEvent(state)) {
    return dispatch(callAPI(GET_EVENT_OLD, getEventId(event)))
  }
}

export const getEvent = createAsyncAction('GET_EVENT', async (_, {getState, extra: {serverApi}}) => {
  const state = getState()
  const event = getEventSelector(state)

  if (!isDemoEvent(state)) {
    return serverApi.getEvent(getEventId(event))
  }

  return event
})

export const getMembersOld =
  () =>
  async (dispatch: Function, getState: GetState, {flowAPI}: StoreExtraArgs) => {
    const state = getState()
    const eventId = getEventSelector(state).id
    const {membersVisible = true} = flowAPI.controllerConfig?.config?.style?.styleParams?.booleans ?? {}

    if (membersVisible && isEventsInMembersInstalled(state) && eventId && !isDemoEvent(state)) {
      try {
        const response = await dispatch(callAPI(GET_MEMBERS_OLD, eventId))
        return response
      } catch (e) {
        return null
      }
    }
  }

export const getMembers = createAsyncAction('GET_MEMBERS', async (_, {getState, extra: {flowAPI, serverApi}}) => {
  const state = getState()
  const eventId = getEventSelector(state).id
  const {membersVisible = true} = flowAPI.controllerConfig?.config?.style?.styleParams?.booleans ?? {}

  if (membersVisible && isEventsInMembersInstalled(state) && eventId && !isDemoEvent(state)) {
    return serverApi.getMembers(eventId)
  }
})

export const shareEvent = createAction<{buttonName: string; origin: string}>('SHARE_EVENT')
